import layoutStyles from '@ds/foundations/layout.module.css';
import { type LayoutElementProps, type LayoutProps, resolveLayoutProps } from '@ds/foundations/layoutProps';
import paddingStyles from '@ds/foundations/padding.module.css';
import { type BlockPaddingProps, resolvePaddingProps } from '@ds/foundations/paddingProps';
import { Slot } from '@ds/foundations/Slot';
import { type StackGap } from '@ds/foundations/tokens';
import clsx from 'clsx';
import React, { forwardRef, type ReactNode } from 'react';

import Col from './Col';
import styles from './Grid.module.css';
import GridDebug from './GridDebug';

/**
 * The elements a `Grid` renders as: a `div`, or a list whose items are its `Grid.Col`s. A landmark
 * (`section`, `main`, `nav`) wraps the grid instead — that costs nothing, because the columns stay its
 * direct children. A list can't wrap it, because its items must be the grid's own children.
 */
export type GridElement = 'div' | 'ul' | 'ol';

/**
 * A layout primitive that establishes a CSS Grid context.
 *
 * `Grid` handles column layout: 4 columns on mobile, 8 on tablet, 12 on desktop,
 * with a fixed column gap (gutter) of 16px on mobile/tablet and 24px on desktop.
 * Row gap is configurable via `rowGap`.
 *
 * `Grid` does not centre content or cap width — wrap it in `Container` for that. It does accept
 * the shared **layout props** (`position`, offsets, size, `overflow`, flex-item props — see
 * `layoutProps.ts`), like every layout primitive, and the **block-axis padding props** (`py`, `pt`,
 * `pb` — see `paddingProps.ts`). There is no inline padding: the grid's left and right edges are the
 * page column's, which `Grid.Col bleed` and the page chrome line up with.
 *
 * Children should be `Grid.Col` elements that declare their column span:
 *
 * @example
 * <Container>
 *     <Grid>
 *         <Grid.Col span={{ mobile: 4, tablet: 4, desktop: 6 }}>Left</Grid.Col>
 *         <Grid.Col span={{ mobile: 4, tablet: 4, desktop: 6 }}>Right</Grid.Col>
 *     </Grid>
 * </Container>
 *
 * @example
 * // Tighter row gap for a dense form layout
 * <Grid rowGap={4}>
 *     <Grid.Col span={{ desktop: 12 }}>...</Grid.Col>
 * </Grid>
 *
 * @example
 * // Semantic list — grid renders as <ul>, columns as <li>
 * <Grid as="ul">
 *     <Grid.Col as="li" span={{ desktop: 3 }}>Card</Grid.Col>
 * </Grid>
 */
export interface GridProps
    extends React.HTMLAttributes<HTMLElement>, LayoutElementProps<GridElement>, BlockPaddingProps, LayoutProps {
    /**
     * Vertical gap between rows using `--space-*` token keys.
     *
     * Pass the numeric suffix of the token name — not a raw pixel value.
     * `rowGap={6}` → `var(--space-6)` → 24px desktop / 12px mobile. `rowGap="section"` is the
     * page-section rhythm (`--stack-section`, 48px desktop / 40px below 1200px) for a grid whose
     * rows are page sections.
     *
     * Defaults to `6` (24px).
     */
    rowGap?: StackGap;
    children?: ReactNode;
}

const GridRoot = forwardRef<HTMLElement, GridProps>(
    ({ as: Tag = 'div', asChild, rowGap = 6, className, style, children, ...others }, ref) => {
        // Block padding props split off first, then the layout props — gated data-* attributes +
        // custom properties read by the shared padding.module.css and layout.module.css.
        const padding = resolvePaddingProps(others);
        const { attrs, vars, rest } = resolveLayoutProps(padding.rest);

        // asChild renders the one child element instead, with these props merged onto it (Slot).
        const Comp: React.ElementType = asChild ? Slot : Tag;

        return (
            <Comp
                ref={ref}
                data-row-gap={rowGap}
                {...padding.attrs}
                {...attrs}
                className={clsx(styles.grid, paddingStyles.padding, layoutStyles.layout, className)}
                style={{ ...padding.vars, ...vars, ...style }}
                {...rest}
            >
                {children}
            </Comp>
        );
    }
);

const Grid = Object.assign(GridRoot, { displayName: 'Grid', Col, Debug: GridDebug });

export { Grid };
export default Grid;
